/**
 * DebugContextPanel - Displays debug info in conversation
 *
 * ➡️ = data going INTO the LLM (prompt)
 * ⬅️ = data coming FROM the LLM (response)
 */

import { Bug } from 'lucide-react'

/**
 * Format data for display - handles both objects and strings properly
 */
function formatData(data) {
  if (!data) return 'null'

  // If it's an object with a 'content' string (LLM response), show content directly
  if (data.content && typeof data.content === 'string') {
    return data.content
  }

  // If it's an object with systemPrompt/userMessage, format nicely
  if (data.systemPrompt !== undefined || data.userMessage !== undefined) {
    let output = ''
    if (data.systemPrompt) {
      output += '=== SYSTEM PROMPT ===\n' + data.systemPrompt + '\n\n'
    }
    if (data.userMessage) {
      output += '=== USER MESSAGE ===\n' + data.userMessage
    }
    return output
  }

  // Default: JSON stringify with proper formatting
  return JSON.stringify(data, null, 2)
}

export default function DebugContextPanel({ debug }) {
  if (!debug) return null

  const { type, title, data } = debug

  // Colors based on type
  const config = {
    prompt: {
      border: 'border-blue-500',
      title: 'text-blue-400',
      bg: 'bg-blue-950/40',
    },
    response: {
      border: 'border-violet-500',
      title: 'text-violet-400',
      bg: 'bg-violet-950/40',
    },
  }

  const c = config[type] || config.prompt

  return (
    <div className={`rounded border-l-4 ${c.border} ${c.bg} my-2 font-mono text-[10px]`}>
      {/* Title */}
      <div className={`px-2 py-1 ${c.title} font-bold border-b border-slate-700/50`}>
        <Bug className="w-3 h-3 inline mr-2" />
        {title}
      </div>

      {/* Content - properly formatted */}
      <pre className="p-2 text-slate-300 whitespace-pre-wrap overflow-x-auto leading-relaxed max-h-[500px] overflow-y-auto">
        {formatData(data)}
      </pre>
    </div>
  )
}
